import { Fragment, type ReactNode, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { cn } from '@/lib/utils.ts';

/** Shared empty set, so an uncontrolled table doesn't allocate one per render. */
const EMPTY_SELECTION: ReadonlySet<string> = new Set<string>();

/**
 * ResponsiveTable — single primitive that flips between a horizontal
 * table at md+ and stacked cards at sm-. Spec §9.2.
 *
 * The desktop form composes shadcn `<Table>`. The mobile form renders
 * each row as a card with column labels prefixed inline, so callers
 * don't have to write the layout twice.
 *
 * Column visibility:
 *   `hideAt: 'mobile'`  — column drops on phone (default for low-info
 *                         columns: timestamps, last-seen, secondary id)
 *   `hideAt: 'desktop'` — column drops on desktop (rare; useful for
 *                         mobile-only "tap-to-call" affordances)
 *   omit                — column shows at both sizes
 *
 * Empty state: `empty` renders inside the table area (full-width on
 * desktop, in the card list on mobile). Pass <EmptyState …> from the
 * primitives.
 *
 * Selection is opt-in and **controlled** — pass `selectedKeys` and
 * `onSelectionChange`. It lives in the page rather than here because the page is
 * what knows when a selection has stopped making sense: rows filtered away,
 * a refetch that dropped one, a bulk action that consumed it. A table holding
 * that state internally would silently keep keys for rows nobody can see.
 *
 * Both breakpoints render simultaneously and write the same `rowKey`, so a row
 * checked on either is one entry in the set, never two.
 *
 * Sorting and pagination remain out of scope — those belong in the consuming
 * page or a future ResponsiveDataTable wrapping a TanStack Table instance.
 */

export interface ResponsiveTableColumn<TRow> {
  /** Stable key for React reconciliation + aria-labelling. */
  key: string;
  /** Header label (also used as the prefix on mobile cards). */
  label: ReactNode;
  /** Cell renderer for one row. */
  render: (row: TRow) => ReactNode;
  /** Hide at the given breakpoint. */
  hideAt?: 'mobile' | 'desktop';
  /** Right-align numeric / monetary / timestamp columns at md+. */
  align?: 'left' | 'right';
  /** Optional extra className applied to the desktop <TableCell>. */
  className?: string;
  /** Hide the label on mobile (for the row's "primary" / title cell). */
  hideMobileLabel?: boolean;
}

export interface ResponsiveTableProps<TRow> {
  columns: readonly ResponsiveTableColumn<TRow>[];
  rows: readonly TRow[];
  rowKey: (row: TRow) => string;
  /** Click handler for the entire row. */
  onRowClick?: (row: TRow) => void;
  /** Optional trailing actions column (e.g. <RowActionMenu />). */
  rowActions?: (row: TRow) => ReactNode;
  /**
   * When provided, rows become expandable: returns the detail content shown
   * below a row when it's open, or `null` to make that particular row
   * non-expandable (no chevron, no toggle). Clicking an expandable row toggles
   * it; a leading chevron column appears automatically. Expansion state is held
   * internally, keyed by `rowKey`.
   */
  renderExpanded?: (row: TRow) => ReactNode;
  /**
   * Enables the selection column. Controlled: supply `selectedKeys` and
   * `onSelectionChange` together.
   */
  selectable?: boolean;
  selectedKeys?: ReadonlySet<string>;
  onSelectionChange?: (keys: ReadonlySet<string>) => void;
  /** Rendered when `rows.length === 0`. */
  empty?: ReactNode;
  /** Loading skeleton — rendered in place of rows when true. */
  isLoading?: boolean;
  className?: string;
}

export function ResponsiveTable<TRow>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowActions,
  renderExpanded,
  selectable,
  selectedKeys,
  onSelectionChange,
  empty,
  isLoading,
  className,
}: ResponsiveTableProps<TRow>) {
  const desktopCols = columns.filter((c) => c.hideAt !== 'desktop');
  const mobileCols = columns.filter((c) => c.hideAt !== 'mobile');

  const expandable = !!renderExpanded;
  const [expandedKeys, setExpandedKeys] = useState<ReadonlySet<string>>(() => new Set());
  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const selection = selectedKeys ?? EMPTY_SELECTION;
  const selectionEnabled = !!selectable && !!onSelectionChange;
  const toggleSelected = (key: string) => {
    if (!onSelectionChange) return;
    const next = new Set(selection);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(next);
  };
  const visibleKeys = rows.map(rowKey);
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selection.has(k));
  const someVisibleSelected = visibleKeys.some((k) => selection.has(k));
  // Select-all acts on what is on screen, not on rows a filter has hidden.
  const toggleAllVisible = () => {
    if (!onSelectionChange) return;
    const next = new Set(selection);
    if (allVisibleSelected) visibleKeys.forEach((k) => next.delete(k));
    else visibleKeys.forEach((k) => next.add(k));
    onSelectionChange(next);
  };

  // colSpan for the full-width detail row: data columns + selection + chevron + actions.
  const desktopColSpan =
    desktopCols.length + (selectionEnabled ? 1 : 0) + (expandable ? 1 : 0) + (rowActions ? 1 : 0);

  // ── Empty / loading short-circuits ──────────────────────────────
  if (isLoading) {
    return <ResponsiveTableSkeleton columnCount={desktopCols.length} className={className} />;
  }
  if (rows.length === 0 && empty) {
    return <div className={className}>{empty}</div>;
  }

  // ── Desktop table ───────────────────────────────────────────────
  const desktopTable = (
    <div className="hidden md:block">
      <Table className={className}>
        <TableHeader>
          <TableRow>
            {selectionEnabled ? (
              <TableHead className="w-10">
                <Checkbox
                  checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
                  onCheckedChange={toggleAllVisible}
                  aria-label={allVisibleSelected ? 'Deselect all rows' : 'Select all rows'}
                />
              </TableHead>
            ) : null}
            {expandable ? <TableHead className="w-8" aria-label="Expand" /> : null}
            {desktopCols.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  col.align === 'right' && 'text-right',
                  col.className,
                )}
              >
                {col.label}
              </TableHead>
            ))}
            {rowActions ? <TableHead className="w-12" aria-label="Actions" /> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const key = rowKey(row);
            const detail = renderExpanded ? renderExpanded(row) : null;
            const rowExpandable = detail != null;
            const isOpen = rowExpandable && expandedKeys.has(key);
            const clickable = rowExpandable || !!onRowClick;
            const handleClick = rowExpandable
              ? () => { toggleExpanded(key); }
              : onRowClick
                ? () => { onRowClick(row); }
                : undefined;
            return (
              <Fragment key={key}>
                <TableRow
                  className={clickable ? 'cursor-pointer' : undefined}
                  onClick={handleClick}
                  aria-expanded={rowExpandable ? isOpen : undefined}
                >
                  {selectionEnabled ? (
                    <TableCell className="w-10 align-middle" onClick={(e) => { e.stopPropagation(); }}>
                      <Checkbox
                        checked={selection.has(key)}
                        onCheckedChange={() => { toggleSelected(key); }}
                        aria-label={`Select row ${key}`}
                      />
                    </TableCell>
                  ) : null}
                  {expandable ? (
                    <TableCell className="w-8 pr-0 align-middle text-muted-foreground">
                      {rowExpandable ? (
                        <ChevronRight
                          className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-90')}
                        />
                      ) : null}
                    </TableCell>
                  ) : null}
                  {desktopCols.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(col.align === 'right' && 'text-right', col.className)}
                    >
                      {col.render(row)}
                    </TableCell>
                  ))}
                  {rowActions ? (
                    <TableCell className="w-12 text-right">{rowActions(row)}</TableCell>
                  ) : null}
                </TableRow>
                {isOpen ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={desktopColSpan} className="bg-bg-subtle/40 p-0">
                      <div className="px-4 py-3">{detail}</div>
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  // ── Mobile card list ────────────────────────────────────────────
  const mobileCards = (
    <ul className={cn('md:hidden space-y-2', className)}>
      {rows.map((row) => {
        const key = rowKey(row);
        const detail = renderExpanded ? renderExpanded(row) : null;
        const rowExpandable = detail != null;
        const isOpen = rowExpandable && expandedKeys.has(key);
        const clickable = rowExpandable || !!onRowClick;
        const activate = rowExpandable
          ? () => { toggleExpanded(key); }
          : onRowClick
            ? () => { onRowClick(row); }
            : undefined;
        return (
          <li key={key}>
            <div
              className={cn(
                'rounded-md border border-border bg-card p-3 transition-colors',
                clickable && 'cursor-pointer hover:bg-bg-subtle active:bg-bg-subtle',
              )}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-expanded={rowExpandable ? isOpen : undefined}
              onClick={activate}
              onKeyDown={
                activate
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        activate();
                      }
                    }
                  : undefined
              }
            >
              <div className="flex flex-col gap-1.5">
                {selectionEnabled ? (
                  <div className="mb-1" onClick={(e) => { e.stopPropagation(); }}>
                    <Checkbox
                      checked={selection.has(key)}
                      onCheckedChange={() => { toggleSelected(key); }}
                      aria-label={`Select row ${key}`}
                    />
                  </div>
                ) : null}
                {mobileCols.map((col, idx) => (
                  <div
                    key={col.key}
                    className={cn(
                      'flex items-baseline gap-2 text-sm',
                      // First column gets prominence — it's the row's name/title.
                      idx === 0 ? 'text-base font-medium' : '',
                    )}
                  >
                    {!col.hideMobileLabel && idx !== 0 ? (
                      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex-shrink-0">
                        {col.label}
                      </span>
                    ) : null}
                    <div className="flex-1 min-w-0">{col.render(row)}</div>
                    {idx === 0 && rowExpandable ? (
                      <ChevronRight
                        className={cn(
                          'w-4 h-4 flex-shrink-0 self-center text-muted-foreground transition-transform',
                          isOpen && 'rotate-90',
                        )}
                      />
                    ) : null}
                  </div>
                ))}
                {isOpen ? (
                  <div className="pt-2 mt-1 border-t border-border-subtle">{detail}</div>
                ) : null}
                {rowActions ? (
                  <div className="flex justify-end pt-1">{rowActions(row)}</div>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {desktopTable}
      {mobileCards}
    </>
  );
}

/**
 * Loading-state skeleton matching the desktop table layout. On mobile,
 * renders a few placeholder cards. Used internally when `isLoading` is
 * truthy; exported so pages can render it during initial fetch without
 * a stale `rows={[]}` flash.
 */
export function ResponsiveTableSkeleton({
  columnCount,
  rowCount = 5,
  className,
}: {
  columnCount: number;
  rowCount?: number;
  className?: string;
}) {
  return (
    <>
      <div className={cn('hidden md:block', className)}>
        <Table>
          <TableHeader>
            <TableRow>
              {Array.from({ length: columnCount }).map((_, i) => (
                <TableHead key={i}>
                  <div className="h-3 w-20 rounded bg-muted animate-pulse" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rowCount }).map((_, r) => (
              <TableRow key={r}>
                {Array.from({ length: columnCount }).map((_, c) => (
                  <TableCell key={c}>
                    <div className="h-3 w-full max-w-[140px] rounded bg-muted animate-pulse" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ul className={cn('md:hidden space-y-2', className)}>
        {Array.from({ length: rowCount }).map((_, i) => (
          <li
            key={i}
            className="rounded-md border border-border bg-card p-3 space-y-2"
          >
            <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
            <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
            <div className="h-3 w-1/3 rounded bg-muted animate-pulse" />
          </li>
        ))}
      </ul>
    </>
  );
}
